import * as Sentry from '@sentry/angular';
import { HttpErrorResponse } from '@angular/common/http';

const DEFAULT_ERR_MESSAGES: { [key: string]: string } = {
  '400': 'The server cannot process the request',
  '401': 'You are unauthorized',
  '403': 'You do not have access to this page',
  '404': 'Requested resource is not found',
  '413': 'Your data is too large',
  '429': 'You are sending too many requests. You are rate limited',
  '500': 'Internal server error acquired',
};

/**
 * Extracts a human-readable error message from various error types.
 * This ensures Sentry receives meaningful text instead of raw objects.
 */
function extractHumanReadableMessage(error: unknown): string {
  // Case 1: Already a simple Error with message
  if (error instanceof Error) {
    return error.message;
  }

  // Case 2: Plain string
  if (typeof error === 'string') {
    return error;
  }

  // Case 3: HttpErrorResponse - extract meaningful message
  if (error instanceof HttpErrorResponse) {
    const payload = error.error;

    if (payload) {
      // Backend returns plain text string
      if (typeof payload === 'string') {
        return payload;
      }

      // Standard { message: string }
      if (payload.message) {
        return payload.message;
      }

      // Custom { status: string } with human-readable text (e.g. "Email already used")
      if (payload.status && typeof payload.status === 'string' && isNaN(Number(payload.status))) {
        return payload.status;
      }

      // Symfony validation style { errors: [...] }
      if (Array.isArray(payload.errors) && payload.errors.length) {
        const firstError = payload.errors[0];
        return typeof firstError === 'string' ? firstError : JSON.stringify(firstError);
      }
    }

    // Fallback to generic mapping by HTTP status code
    const defaultMessage = DEFAULT_ERR_MESSAGES[error.status?.toString()];
    if (defaultMessage) {
      return defaultMessage;
    }

    return `HTTP Error ${error.status}: ${error.statusText || 'Unknown error'}`;
  }

  // Case 4: Unknown error type
  return 'Unknown error occurred';
}

/**
 * Unified helper to report handled errors to Sentry and log them in console.
 * Call this in any catch block, catchError or promise rejection handler.
 *
 * Automatically extracts human-readable error messages from HttpErrorResponse
 * and other error types before sending to Sentry.
 *
 * @param error   - any thrown value
 * @param context - optional context that will be attached as extra information in Sentry scope
 */
export function handleError(error: unknown, context?: Record<string, unknown>): void {
  const humanMessage = extractHumanReadableMessage(error);

  // Send human-readable message to Sentry as the main error
  // Also attach the original error as extra context
  Sentry.captureException(new Error(humanMessage), scope => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value as any);
      });
    }
    
    // Attach original error details for debugging
    scope.setExtra('originalError', error);
    
    // If it's an HttpErrorResponse, add useful HTTP context
    if (error instanceof HttpErrorResponse) {
      scope.setExtra('httpStatus', error.status);
      scope.setExtra('httpStatusText', error.statusText);
      scope.setExtra('httpUrl', error.url);
    }
    
    return scope;
  });
  
  // eslint-disable-next-line no-console
  console.error('Reported error:', humanMessage, error, context ?? '');
}

/**
 * RxJS operator for convenient error reporting.
 *
 * Usage:
 *   this.http.get(...).pipe(reportOnError({ source: 'loadProfile' }))
 */
import { catchError, throwError } from 'rxjs';
export const reportOnError = <T>(context?: Record<string, unknown>) =>
  catchError((err: unknown) => {
    handleError(err, context);
    return throwError(() => err);
  });
